import { useEffect, useRef, useState } from 'react'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { NoteWysiwygEditor } from '../../pages/Warehouse/Devannings/NoteWysiwygEditor'
import { btnGreen, btnSecondary, btnPrimary, inputCls } from '../../pages/Warehouse/types'
import type { DeliveryNote } from '../../pages/Warehouse/types'
import { getCompanySettings, type CompanySettings } from '../../lib/companySettings'
import { resolveBoxtopEmployeeName } from '../../lib/transportNote'
import type { DeliveryMethodRow } from '../../lib/fulfilmentType'
import { blankNoteDraft, noteDraftFromNote, resolveNote, standaloneAuto, buildNoteRow, buildNoteData, type NoteDraft } from '../../lib/deliveryNoteDraft'
import { saveNoteRow, generateNotePdf } from '../../lib/deliveryNoteSave'

// Job -> Transport: raise / edit a Collection/Delivery Note for a container move that does not go through the warehouse (port <-> warehouse,
// port -> customer). Z44: this is the SAME editor, row builder and PDF as the warehouse's note (NoteWysiwygEditor + lib/deliveryNoteDraft.ts +
// DeliveryCollectionNotePDF); the only difference is that there is no devanning to derive quantities from, so everything is typed in
// (plain mode). Stored in delivery_notes with note_source = 'HAULAGE', which keeps these notes off the warehouse boards.
// Only DRAFT notes are editable; once pushed, changes go through Void + a new note, same as warehouse notes.
interface JobContainer { id: string; container_no: string }

// "Container: X - SEAL: Y - 40HQ": the Remarks line on a port note. Seal and type come from the job's own container record.
async function containerRemarks(jobId: string, containerNo: string): Promise<string> {
  const { data } = await supabase.from('job_containers')
    .select('container_no, seal_no, container_types(code)').eq('job_id', jobId).eq('container_no', containerNo).maybeSingle()
  const c = data as { container_no: string; seal_no: string | null; container_types: { code: string } | { code: string }[] | null } | null
  const type = Array.isArray(c?.container_types) ? c?.container_types[0]?.code : c?.container_types?.code
  return [`Container: ${containerNo}`, c?.seal_no ? `SEAL: ${c.seal_no}` : null, type || null].filter(Boolean).join(' - ')
}

export default function TransportNoteDialog({ jobId, jobRef, editing, prefill, onClose, onSaved }: {
  jobId: string
  jobRef: string
  editing: DeliveryNote | null
  // A starting draft for a NEW note (e.g. carried over from a group row). Everything in it stays editable.
  prefill?: NoteDraft | null
  onClose: () => void
  onSaved: () => void
}) {
  const { user, userEmail } = useAuth()
  const [draft, setDraft] = useState<NoteDraft>(() => editing ? { ...noteDraftFromNote(editing, null), reissueOf: null } : (prefill ?? blankNoteDraft('COLLECTION_COURIER', null, '', null)))
  const [noteId, setNoteId] = useState<string | null>(editing?.id ?? null)
  const [serial, setSerial] = useState<number | null>(editing?.serial_no ?? null)
  const [containerNo, setContainerNo] = useState<string>(editing?.container_no ?? '')
  const [containers, setContainers] = useState<JobContainer[]>([])
  const [methods, setMethods] = useState<DeliveryMethodRow[]>([])
  const [company, setCompany] = useState<CompanySettings | null>(null)
  const [issuerName, setIssuerName] = useState('')
  const [busy, setBusy] = useState<'save' | 'pdf' | 'push' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const methodsFixedRef = useRef(false)

  useEffect(() => {
    getCompanySettings().then(setCompany)
    resolveBoxtopEmployeeName(userEmail).then(setIssuerName)
    supabase.from('pricing_delivery_methods').select('id, code, name, default_fulfilment_type').eq('is_active', true).order('name')
      .then(({ data }) => setMethods((data as DeliveryMethodRow[] | null) || []))
    supabase.from('job_containers').select('id, container_no').eq('job_id', jobId)
      .then(({ data }) => setContainers(((data as JobContainer[] | null) || []).filter(c => c.container_no)))
  }, [jobId, userEmail])

  // A note saved before delivery_method_id existed has no method yet: map its note_type back to one, once the list is loaded.
  useEffect(() => {
    if (methodsFixedRef.current || methods.length === 0) return
    methodsFixedRef.current = true
    setDraft(d => {
      if (d.deliveryMethodId) return d
      const code = d.noteType === 'COLLECTION_SELF' ? 'SELF_PICKUP' : d.noteType === 'COLLECTION_SUPPLIER' ? 'SUPPLIER_PICKUP' : editing ? 'COURIER' : null
      const m = code ? methods.find(x => x.code === code) : undefined
      return m ? { ...d, deliveryMethodId: m.id } : d
    })
  }, [methods, editing])

  const resolved = resolveNote(draft, methods, [])
  const cd = resolved.fulfilment.type === 'COLLECTION_AND_DELIVERY'
  const patch = (p: Partial<NoteDraft>) => setDraft(d => ({ ...d, ...p }))

  async function pickContainer(no: string) {
    setContainerNo(no)
    if (no && !(draft.remarksOverride ?? '').trim()) patch({ remarksOverride: await containerRemarks(jobId, no) })
  }

  function validate(): string | null {
    if (!draft.collectFrom.name) return 'Collect From is required.'
    if (!draft.releaseTo.name) return 'Release To is required.'
    if (resolved.fulfilment.type === null) return 'Choose the Fulfilment (Collection Only / Collection + Delivery) at the top of the note.'
    if (cd && !draft.deliverTo.name) return 'Deliver To is required for a Collection + Delivery note.'
    if (!draft.scheduledDate) return 'Collection date is required.'
    const auto = standaloneAuto(draft)
    if (!((draft.nopOverride ?? 0) > 0 || auto.pallets > 0)) return 'Enter a quantity (NOP or pallets in the Summary box).'
    return null
  }

  // Row = the shared builder + what only this tab knows. Insert on first save (the database assigns id and serial_no), update after.
  async function persist(): Promise<DeliveryNote> {
    const row = buildNoteRow({
      draft, auto: standaloneAuto(draft), resolved, jobRef, devanningId: null,
      issuer: { userId: user?.id || null, name: issuerName }, previousNote: editing,
    })
    const extra = { note_source: 'HAULAGE', job_id: jobId, container_no: containerNo.trim() || null, transit_status: null }
    const saved = await saveNoteRow(noteId, noteId ? { ...row, ...extra } : { ...row, ...extra, lifecycle_status: 'DRAFT' })
    setNoteId(saved.id); setSerial(saved.serial_no)
    return saved
  }

  async function run(kind: 'save' | 'pdf' | 'push') {
    const v = validate()
    if (v) { setError(v); return }
    if (!company) { setError('Company settings are still loading, try again in a moment.'); return }
    setBusy(kind); setError(null)
    try {
      const saved = await persist()
      if (kind !== 'save') {
        const issuedAt = new Date().toISOString()
        await generateNotePdf({
          saved,
          data: buildNoteData({ draft, auto: standaloneAuto(draft), resolved, jobRef: saved.job_ref, company, serialNo: saved.serial_no, issuerName, issuedAt }),
          folder: saved.job_ref, filePrefix: 'HAULAGE', issuedAt, userId: user?.id || null, issuerName,
        })
      }
      if (kind === 'push') {
        const { error: e } = await supabase.from('delivery_notes').update({
          lifecycle_status: 'PLANNED', planned_at: new Date().toISOString(), planned_by: user?.id || null,
          transit_status: cd ? 'PENDING' : null,
        }).eq('id', saved.id)
        if (e) throw e
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : (err as { message?: string })?.message || 'Save failed')
    } finally { setBusy(null) }
  }

  const containerPicker = (
    <div className="mb-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5">
      <span className="font-medium">Container</span>
      {containers.length > 0 && (
        <select className="border rounded px-1.5 py-1 text-xs dark:bg-gray-700" value={containers.some(c => c.container_no === containerNo) ? containerNo : ''}
          onChange={e => e.target.value && pickContainer(e.target.value)}>
          <option value="">Pick from this job…</option>
          {containers.map(c => <option key={c.id} value={c.container_no}>{c.container_no}</option>)}
        </select>
      )}
      <input className={`${inputCls} !w-44 !py-1 !text-xs`} placeholder="Container no." value={containerNo} onChange={e => setContainerNo(e.target.value.toUpperCase())} />
      <span className="text-gray-400">Picking one fills Remarks with its seal and type.</span>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-6xl my-6">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{serial ? `Edit Collection/Delivery Note #${serial}` : 'New Collection/Delivery Note'}</h3>
            <p className="text-xs text-gray-500">{jobRef} · port ↔ warehouse or port → customer. Not part of the warehouse flow. Quantities are typed in.</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="px-4 md:px-6 py-4">
          {company ? (
            <NoteWysiwygEditor
              draft={draft} onChange={patch} company={company} jobRef={jobRef} serialNo={serial} issuerName={issuerName}
              methods={methods} plain headerExtra={containerPicker} containerNo={containerNo || null}
            />
          ) : <p className="text-sm text-gray-400 py-8 text-center">Loading…</p>}
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>

        <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
          <button onClick={onClose} className={btnSecondary}>Cancel</button>
          <button onClick={() => run('save')} disabled={!!busy} className={btnSecondary}>{busy === 'save' ? 'Saving…' : 'Save Draft'}</button>
          <button onClick={() => run('pdf')} disabled={!!busy} className={btnPrimary}>{busy === 'pdf' ? 'Generating…' : 'Save & Generate PDF'}</button>
          <button onClick={() => run('push')} disabled={!!busy} className={btnGreen}>{busy === 'push' ? 'Pushing…' : 'Push to Planned'}</button>
        </div>
      </div>
    </div>
  )
}
